import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { useAuthStore } from '@/store/auth-store';
import { getLanguage, i18n } from '@/lib/language';
import {
  retryPushRemovals,
  revokePushRegistration,
  uploadPushRegistration,
} from './push-registration';

let cleanupPushListeners: (() => Promise<void>) | undefined;

export type PushTapData = { link?: string; notification_id?: string };

export async function initPushNotifications(
  onNotificationTap: (data: PushTapData) => void
): Promise<void> {
  const platform = Capacitor.getPlatform();
  if (cleanupPushListeners || (platform !== 'android' && platform !== 'ios')) return;
  const fcmRegistration = { platform, tokenType: 'fcm' } as const;

  const listeners: PluginListenerHandle[] = [];
  let disposed = false;
  let registering = false;
  let prompted = false;
  let unsubscribeAuthChanges = () => {};
  let fcmToken: string | undefined;
  let permissionGranted = false;
  let apnsReady = platform !== 'ios';
  let listenersReady = false;

  async function uploadFcmToken(): Promise<void> {
    const session = useAuthStore.getState().token;
    if (disposed || !session || !fcmToken || !permissionGranted || !apnsReady) return;
    try {
      await uploadPushRegistration(
        fcmToken,
        session,
        () => !disposed && permissionGranted && useAuthStore.getState().token === session,
        fcmRegistration
      );
    } catch {
      console.warn('[push] Token upload failed; retry on app resume or connectivity recovery.');
    }
  }

  const recoverPushRegistration = async () => {
    try {
      await retryPushRemovals(!useAuthStore.getState().token);
      await registerForPushNotifications(false);
    } catch {
      console.warn('[push] Server removal pending; retry on the next reconnect or app resume.');
    }
  };

  cleanupPushListeners = async () => {
    disposed = true;
    unsubscribeAuthChanges();
    window.removeEventListener('online', recoverPushRegistration);
    await Promise.all(listeners.map((listener) => listener.remove()));
    cleanupPushListeners = undefined;
  };

  async function registerForPushNotifications(allowPrompt: boolean): Promise<void> {
    const session = useAuthStore.getState().token;
    if (disposed || registering || !session) return;
    registering = true;
    try {
      await retryPushRemovals();
      let permission = await FirebaseMessaging.checkPermissions();
      if (allowPrompt && !prompted && permission.receive === 'prompt') {
        prompted = true;
        permission = await FirebaseMessaging.requestPermissions();
      }
      permissionGranted = permission.receive === 'granted';
      if (
        !disposed &&
        useAuthStore.getState().token === session &&
        permission.receive === 'granted' &&
        apnsReady
      ) {
        const { token } = await FirebaseMessaging.getToken();
        if (!disposed && useAuthStore.getState().token === session) {
          fcmToken = token;
          await uploadFcmToken();
        }
      } else if (permission.receive === 'denied') {
        fcmToken = undefined;
        revokePushRegistration();
      }
    } catch {
      console.warn('[push] Registration failed; retry on the next login or app resume.');
    } finally {
      registering = false;
      if (!disposed && useAuthStore.getState().token && useAuthStore.getState().token !== session) {
        void registerForPushNotifications(false);
      }
    }
  }

  async function trackListener(handle: Promise<PluginListenerHandle>): Promise<void> {
    const listener = await handle;
    if (disposed) {
      await listener.remove();
      throw new Error('Push initialization disposed');
    }
    listeners.push(listener);
  }

  try {
    await trackListener(
      FirebaseMessaging.addListener('tokenReceived', ({ token }) => {
        fcmToken = token;
        void uploadFcmToken();
      })
    );
    if (platform === 'ios') {
      await trackListener(
        FirebaseMessaging.addListener('apnsTokenReceived', () => {
          apnsReady = true;
          if (listenersReady) void registerForPushNotifications(false);
        })
      );
    }
    await trackListener(FirebaseMessaging.addListener('notificationReceived', () => {}));
    await trackListener(
      FirebaseMessaging.addListener('notificationActionPerformed', ({ notification }) => {
        if (!disposed) onNotificationTap((notification.data ?? {}) as PushTapData);
      })
    );
    if (platform === 'android')
      await FirebaseMessaging.createChannel({
        id: 'learning_updates',
        name: i18n.getFixedT(getLanguage(), 'common')('pushChannelName'),
        description: i18n.getFixedT(getLanguage(), 'common')('pushChannelDescription'),
        importance: 4,
        visibility: 0,
      });
    await trackListener(
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void recoverPushRegistration();
      })
    );
    unsubscribeAuthChanges = useAuthStore.subscribe((state, previous) => {
      if (!state.token) fcmToken = undefined;
      if (state.token && state.token !== previous.token) void registerForPushNotifications(true);
    });
    window.addEventListener('online', recoverPushRegistration);
    listenersReady = true;
    if (useAuthStore.getState().token) await registerForPushNotifications(true);
    else await recoverPushRegistration();
  } catch {
    console.warn('[push] Initialization failed.');
    await cleanupPushListeners?.();
  }
}

export async function stopPushNotifications(): Promise<void> {
  await cleanupPushListeners?.();
}
